/**
 * HaveDataDateRange 组件
 * 展示带有"最新有数据日期"智能计算的时间范围标题栏
 *
 * 功能说明：
 * 1. 根据筛选器时间范围和趋势数据，智能计算并展示实际有数据的时间范围
 * 2. 支持可选的标题和切换插槽（switchSlot）
 * 3. 切换插槽支持 byDataKey 和默认两种渲染模式
 */
import React, { useContext, useMemo } from 'react';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { useLocal } from '../../utils/customHook';
import Abnormal from '@home/components/Abnormal';
import SwitchSlotHeader from './SwitchSlotHeader';
import SwitchSlotChildren from './SwitchSlotChildren';
import { mergeTrendLists, findLatestDataDate, calcDisplayDateRange } from './utils';
import { HaveDataDateRangeProps } from './type';
import './index.scss';

export default function HaveDataDateRange(props: HaveDataDateRangeProps) {
  const {
    style,
    className = '',
    keyMetricsTrend = [],
    data,
    titleEn,
    titleCn,
    needTitle = false,
    switchSlot,
    loading,
    error,
    isChange,
    switchDataLoadingHeight = 454,
    loadingTargetNames,
    changeData,
  } = props || {};

  const switchData = props?.switchData || { cardTitleSwitchVal: data?.cardTitleSwitchVal };
  const contextObj = useContext(FilterContext);
  const { filterVal = {} } = contextObj || {};
  const { rangeTime = [], timeType = '', timeZone = 0 } = filterVal || {};
  const dateFormat = timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD';
  const { isEN } = useLocal();
  const title = isEN ? titleEn : titleCn;

  const switchSlotProps = switchSlot?.props || {};
  const shouldRenderByDataKey = switchSlotProps?.childrenRenderMode === 'byDataKey';

  // 合并 props 传入的 trend 和 data 中的 trend
  const allTrendData = useMemo(() => {
    const baseTrend = keyMetricsTrend?.length > 0 ? [...keyMetricsTrend] : [];
    const extraTrend = mergeTrendLists(data?.keyMetricsData?.data);
    return [...baseTrend, ...extraTrend];
  }, [keyMetricsTrend, data?.keyMetricsData?.data]);

  // 从合并后的 trend 中找到最新有数据的日期
  const haveDataDate = useMemo(
    () => findLatestDataDate(allTrendData),
    [allTrendData],
  );

  // 计算最终展示的时间范围
  const displayDateRange = useMemo(
    () => calcDisplayDateRange(
      allTrendData.length > 0 ? haveDataDate : null,
      rangeTime as string[],
      timeType as string,
      Number(timeZone),
      dateFormat,
    ),
    [allTrendData.length, haveDataDate, rangeTime, timeType, timeZone, dateFormat],
  );

  return (
    <>
      {/* 时间范围标题栏 */}
      <div className={`metrics-date-range-last ${className}`} style={style}>
        {(needTitle || switchSlot) && (
          <div className="metrics-date-range-last-title-box">
            {needTitle && <div className="metrics-date-range-last-title">{title}</div>}
            {switchSlot && (
              <SwitchSlotHeader
                switchSlot={switchSlot}
                shouldRenderByDataKey={shouldRenderByDataKey}
                data={data}
                switchData={switchData}
                changeData={changeData}
              />
            )}
          </div>
        )}
        <div
          className="metrics-date-range-last-time"
          style={{ color: needTitle ? undefined : '#262832' }}
        >
          {displayDateRange}
        </div>
      </div>

      {/* 切换插槽的子组件内容区域 */}
      {switchSlot && (
        loadingTargetNames?.length ? (
          <SwitchSlotChildren
            switchSlot={switchSlot}
            switchSlotProps={switchSlotProps}
            shouldRenderByDataKey={shouldRenderByDataKey}
            data={data}
            switchData={switchData}
            isChange={isChange}
            loading={loading}
            error={error}
            loadingTargetNames={loadingTargetNames}
            switchDataLoadingHeight={switchDataLoadingHeight}
            changeData={changeData}
          />
        ) : (
          <Abnormal
            isLoading={loading === undefined ? false : loading}
            isError={error}
            style={{ background: '#fff', height: switchDataLoadingHeight, borderRadius: 8 }}
            component={
              <SwitchSlotChildren
                switchSlot={switchSlot}
                switchSlotProps={switchSlotProps}
                shouldRenderByDataKey={shouldRenderByDataKey}
                data={data}
                switchData={switchData}
                isChange={isChange}
                changeData={changeData}
              />
            }
          />
        )
      )}
    </>
  );
}
